import { formatCurrency, type CurrencyCodes, type CountryCodes } from 'src/shared/utils/currency';
import { AMOUNT_TOOLTIP_LABELS, COUNT_TOOLTIP_LABELS } from './utils';
import i18n from 'src/i18n';
import {
  type BarChartOptions,
  type LineChartOptions,
  type CountTooltipLabelData,
  type AmountTooltipLabelData
} from './types';
import { COUNT_FOR_PLURAL_TRANSLATION } from 'src/shared/helpers/chartHelpers';
import { type TooltipItem } from 'chart.js';

export const tooltipTotalReducerFunction = (
  originalValue: number,
  rawValue: TooltipItem<'bar'> | TooltipItem<'line'>
) => (originalValue += (rawValue.raw as number) ? Number(rawValue.raw) : 0);
export const barChartOptions = (
  isStacked: boolean,
  theme: any,
  currencyCode: CurrencyCodes,
  countryCode: CountryCodes
): BarChartOptions => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: undefined,
    layout: {
      padding: 0
    },
    scales: {
      x: {
        stacked: isStacked,
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          padding: 20,
          color: theme.palette.text.secondary
        }
      },
      y: {
        stacked: isStacked,
        min: 0,
        beginAtZero: true,
        border: {
          dash: [2],
          dashOffset: 2,
          display: false
        },
        ticks: {
          padding: 20,
          color: theme.palette.text.secondary,
          maxTicksLimit: 7,
          callback: value => {
            return formatCurrency(countryCode, currencyCode)(Number(value));
          }
        }
      }
    },
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        caretSize: 10,
        padding: {
          y: 20,
          x: 20
        },
        borderWidth: 1,
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.common.white,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        footerColor: theme.palette.text.secondary,
        callbacks: {
          label: tooltipItem => {
            const label = tooltipItem?.dataset.label as keyof AmountTooltipLabelData;
            if (!label || tooltipItem.dataset.data[tooltipItem.dataIndex] === undefined) {
              return '';
            }

            return `${i18n.t(AMOUNT_TOOLTIP_LABELS[label], {
              ns: 'common',
              count: COUNT_FOR_PLURAL_TRANSLATION
            })}: ${formatCurrency(countryCode, currencyCode)(Number(tooltipItem.raw))}`;
          },
          afterBody: (tooltipItem: Array<TooltipItem<'bar'>>) => {
            return `Total: ${formatCurrency(
              countryCode,
              currencyCode
            )(tooltipItem.reduce(tooltipTotalReducerFunction, 0))}`;
          }
        }
      },
      legend: {
        display: false
      }
    }
  };
};

export const lineChartOptions = (theme: any, isStacked: boolean): LineChartOptions => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: undefined,
    layout: {
      padding: 0
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          padding: 20,
          color: theme.palette.text.secondary
        }
      },
      y: {
        stacked: isStacked,
        beginAtZero: true,
        border: {
          dash: [2],
          dashOffset: 2,
          color: theme.palette.divider,
          display: false
        },
        ticks: {
          padding: 20,
          color: theme.palette.text.secondary,
          maxTicksLimit: 7
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        position: 'nearest',
        intersect: false,
        caretSize: 10,
        padding: {
          x: 20,
          y: 20
        },
        borderWidth: 1,
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.common.white,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        footerColor: theme.palette.text.secondary,
        callbacks: {
          label: tooltipItem => {
            const label = tooltipItem?.dataset.label as keyof CountTooltipLabelData;
            if (!label || label === COUNT_TOOLTIP_LABELS.summary) {
              return '';
            }
            return `${i18n.t(COUNT_TOOLTIP_LABELS[label], {
              ns: 'common',
              count: COUNT_FOR_PLURAL_TRANSLATION
            })}: ${Number(tooltipItem.raw)}`;
          },
          afterBody: (tooltipItems: Array<TooltipItem<'line'>>) => {
            const summaryTooltipItem = tooltipItems[tooltipItems.length - 1];
            return `Total: ${summaryTooltipItem.raw}`;
          }
        }
      }
    },
    elements: {
      line: {
        cubicInterpolationMode: 'monotone'
      },
      point: {
        borderColor: '#FFFFFF',
        hoverBorderColor: '#FFFFFF'
      }
    }
  };
};
